import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { clearError, loginUser, logout } from '../store/slices/authSlice';
import AuthShowcase from '../components/auth/AuthShowcase';
import { ROUTES } from '../config/appConfig';
import { normalizeUserRole } from '../utils/apiResponseUtils';
import '../styles/Auth.css';

const validationSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const showcaseFeatures = [
  {
    title: 'Driver screening',
    description: 'See vehicle, license, and contact details in one approval workflow.',
  },
  {
    title: 'Trip protection',
    description: 'Unapproved drivers stay out of live orders and delivery details.',
  },
  {
    title: 'Single admin identity',
    description: 'Use the admin username with role set to admin for secure access.',
  },
];

const AdminLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useSelector((state) => state.auth);

  React.useEffect(() => {
    const role = normalizeUserRole(user);

    if (isAuthenticated && role === 'admin') {
      navigate(ROUTES.adminDashboard);
      return;
    }

    if (isAuthenticated && role !== 'admin') {
      dispatch(logout());
    }
  }, [dispatch, isAuthenticated, navigate, user]);

  return (
    <div className="auth-page driver-auth-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} xl={11}>
            <div className="auth-layout auth-layout-driver">
              <AuthShowcase
                variant="driver"
                platformLabel="Admin Control Room"
                kicker="Admin Access"
                title="Review drivers before they enter the live trip queue."
                description="Approve driver accounts, inspect registration details, and keep trip access locked until verification is complete."
                features={showcaseFeatures}
              />

              <div className="auth-card auth-form-card p-4 rounded shadow">
                <div className="auth-heading-block">
                  <span className="auth-chip driver-chip-auth">Admin Login</span>
                  <h2 className="mb-2 fw-bold">Enter the approval workspace</h2>
                  <p className="text-muted mb-0">Use username <strong>admin</strong> and your admin password to continue.</p>
                </div>

                {error && (
                  <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
                    {error}
                  </Alert>
                )}

                <Formik
                  initialValues={{ username: 'admin', password: '' }}
                  validationSchema={validationSchema}
                  onSubmit={async (values) => {
                    const result = await dispatch(loginUser({ ...values, role: 'admin' }));

                    if (result.payload && !result.error) {
                      navigate(ROUTES.adminDashboard);
                    }
                  }}
                >
                  {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          value={values.username}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.username && !!errors.username}
                          placeholder="admin"
                        />
                        <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.password && !!errors.password}
                          placeholder="Enter admin password"
                        />
                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                      </Form.Group>

                      <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Signing in...
                          </>
                        ) : (
                          'Login as Admin'
                        )}
                      </Button>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminLogin;
